import { useCallback, useRef, useState } from 'react'

interface UsePeerReconnectionOptions {
  isInitiator(peerId: ID): boolean
  onReconnectAttempt(peerId: ID): void
  onGiveUp(peerId: ID): void
}

export const PEER_DROP_TIMEOUT_MS = 10_000 // 10 segundos

export function usePeerReconnection({ isInitiator, onReconnectAttempt, onGiveUp }: UsePeerReconnectionOptions) {
  const giveUpTimers = useRef<Map<ID, ReturnType<typeof setTimeout>>>(new Map())
  const reconnecting = useRef<Set<ID>>(new Set())
  const giveUpSuspended = useRef(false)

  const [hasReconnectingPeers, setHasReconnectingPeers] = useState(false)
  const syncHasReconnecting = useCallback(() => setHasReconnectingPeers(reconnecting.current.size > 0), [])

  const stopPeer = useCallback(
    (peerId: ID) => {
      const giveUp = giveUpTimers.current.get(peerId)
      if (giveUp) {
        clearTimeout(giveUp)
        giveUpTimers.current.delete(peerId)
      }
      if (reconnecting.current.delete(peerId)) syncHasReconnecting()
    },
    [syncHasReconnecting]
  )

  const armGiveUpTimer = useCallback(
    (peerId: ID) => {
      giveUpTimers.current.set(
        peerId,
        setTimeout(() => {
          stopPeer(peerId)
          onGiveUp(peerId)
        }, PEER_DROP_TIMEOUT_MS)
      )
    },
    [onGiveUp, stopPeer]
  )

  const handleIceState = useCallback(
    (peerId: ID, state: RTCIceConnectionState) => {
      if (state === 'disconnected' || state === 'failed') {
        if (!reconnecting.current.has(peerId)) {
          reconnecting.current.add(peerId)
          syncHasReconnecting()
          if (!giveUpSuspended.current) armGiveUpTimer(peerId)
        }

        if (state === 'failed' && isInitiator(peerId)) onReconnectAttempt(peerId)
      } else if (state === 'connected' || state === 'completed') {
        stopPeer(peerId)
      }
    },
    [armGiveUpTimer, isInitiator, onReconnectAttempt, stopPeer, syncHasReconnecting]
  )

  const setGiveUpSuspended = useCallback(
    (suspended: boolean) => {
      if (giveUpSuspended.current === suspended) return
      giveUpSuspended.current = suspended
      if (suspended) {
        giveUpTimers.current.forEach((timer) => clearTimeout(timer))
        giveUpTimers.current.clear()
      } else {
        reconnecting.current.forEach((peerId) => armGiveUpTimer(peerId))
      }
    },
    [armGiveUpTimer]
  )

  const isPeerReconnecting = useCallback((peerId: ID) => reconnecting.current.has(peerId), [])

  const clearAll = useCallback(() => {
    giveUpTimers.current.forEach((timer) => clearTimeout(timer))
    giveUpTimers.current.clear()
    reconnecting.current.clear()
    setHasReconnectingPeers(false)
  }, [])

  return {
    handleIceState,
    hasReconnectingPeers,
    isPeerReconnecting,
    setGiveUpSuspended,
    cancelReconnection: stopPeer,
    clearAll,
  }
}
